import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useAppDispatch } from 'hooks/reduxHooks'
import { userCartItemsHardDelete, userCartItemUndo } from 'actions/AuthActions'
import styled from 'styled-components'
import TextButton from 'components/Luxkit/Button/TextButton'
import LineHistoryIcon from 'components/Luxkit/Icons/line/LineHistoryIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import * as Analytics from 'analytics/analytics'
import debounce from 'debounce-promise'

const DeletedTileContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${rem(12)};
  padding: ${rem(20)};
  border-radius: ${props => props.theme.borderRadius.S};
  background: ${props => props.theme.palette.neutral.default.seven};

  ${mediaQueryUp.tablet} {
    border: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
`

const TruncatedBodyText = styled(BodyText)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const DEFAULT_DELAY_IN_SECONDS = 5
interface Props {
  title: string
  itemIds: Array<string>
}

function DeletedCartReviewTile({ title, itemIds }: Props) {
  const dispatch = useAppDispatch()
  const remove = useRef<boolean>(true)

  const hardDelete = useCallback(() => {
    if (remove.current) {
      dispatch(userCartItemsHardDelete(itemIds))
      remove.current = false
    }
  }, [dispatch, itemIds])

  const hardDeleteDebounced = useMemo(() => debounce(hardDelete, DEFAULT_DELAY_IN_SECONDS * 1000), [hardDelete])

  useEffect(() => {
    hardDeleteDebounced()
    return () => hardDelete()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleUndo = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'undo_delete_item',
      action: 'click',
      category: 'multi_item_cart',
      type: 'interaction',
    })
    remove.current = false
    dispatch(userCartItemUndo(itemIds))
  }, [dispatch, itemIds])

  return (
    <DeletedTileContainer>
      <Group direction="vertical" gap={4}>
        <TruncatedBodyText variant="medium" weight="bold">{title}</TruncatedBodyText>
        <BodyText variant="medium" colour="neutral-two">was deleted from your cart</BodyText>
      </Group>
      <TextButton
        kind="secondary"
        size="small"
        startIcon={<LineHistoryIcon />}
        onClick={handleUndo}
      >
        Undo
      </TextButton>
    </DeletedTileContainer>
  )
}

export default DeletedCartReviewTile
